import { useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { JobRun } from "~/components/job-log";
import {
  SubmitDataImportPipelineJobDocument,
  type SubmitDataImportPipelineJobMutation,
  type SubmitDataImportPipelineJobMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { DataImportPipelineJobHistoryService } from "~/features/dataImportProjects/services/dataImportPipelineJobHistory.service";
import type { DataImportProjectPipelineJobHistoryItem } from "~/features/dataImportProjects/types";
import {
  type DipPipelineDryRunResultTarget,
  type DipRunStatus,
  toPipelineDryRunResultTarget,
} from "~/features/dataImportProjects/types";
import { awaitRunOutcomeFromLogs } from "~/features/dataImportProjects/utils/awaitRunOutcomeFromLogs";
import { useTenant } from "~/features/tenant/context";

type DipRunAction = "run" | "dryrun";

export type DipRunAlert = {
  type: "success" | "error";
  titleKey: string;
  jobRunId?: string;
  dryRunResult?: DipPipelineDryRunResultTarget;
} | null;

interface DipRunOptions {
  autoApprove?: boolean;
  logLevel?: string;
  dryRunOptions?: { limit?: number; offset?: number };
}

interface UseDipPipelineRunProps {
  projectId: string;
  /** Refetch all pipeline queries after a committing run to surface new counts. */
  onRefresh: () => Promise<unknown>;
  setLogDialogJobRun?: (jobRun: JobRun | null) => void;
}

interface UseDipPipelineRunReturn {
  running: boolean;
  /** Which action is currently in progress (drives the per-button "Running…" state). */
  runningAction: DipRunAction | null;
  /** jobId -> overlay status for the current/last run. Empty when idle. */
  statusMap: Map<string, DipRunStatus>;
  alert: DipRunAlert;
  setAlert: (a: DipRunAlert) => void;
  run: (action: DipRunAction, beJobIds: string[], bcrJobIds: string[], options: DipRunOptions) => Promise<void>;
}

export function useDipPipelineRun({
  projectId,
  onRefresh,
  setLogDialogJobRun,
}: UseDipPipelineRunProps): UseDipPipelineRunReturn {
  const { tenantId } = useTenant();
  const historyService = useRef(new DataImportPipelineJobHistoryService()).current;
  const [submitMutation] = useMutation<
    SubmitDataImportPipelineJobMutation,
    SubmitDataImportPipelineJobMutationVariables
  >(SubmitDataImportPipelineJobDocument);

  const [runningAction, setRunningAction] = useState<DipRunAction | null>(null);
  const [statusMap, setStatusMap] = useState<Map<string, DipRunStatus>>(new Map());
  const [alert, setAlert] = useState<DipRunAlert>(null);

  // AbortController to cancel awaitRunOutcomeFromLogs on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const run = useCallback(
    async (action: DipRunAction, beJobIds: string[], bcrJobIds: string[], options: DipRunOptions) => {
      if (runningAction !== null || !tenantId) return;
      const dryRun = action === "dryrun";
      setRunningAction(action);
      setAlert(null);
      // queue all selected jobs
      setStatusMap(new Map([...beJobIds, ...bcrJobIds].map((id) => [id, "queued" as DipRunStatus])));

      try {
        const { data } = await submitMutation({
          variables: {
            input: {
              tenantId,
              batchEvidencesJobIds: beJobIds,
              batchChangeRequestsJobIds: bcrJobIds,
              dryRun,
              dryRunOptions: options.dryRunOptions,
              autoApprove: options.autoApprove,
              logLevel: options.logLevel,
            },
          },
        });
        const ret = data?.cnutSubmitDataImportPipelineJob;
        if (ret?.__typename === "ClientError") {
          throw new Error(`${ret.message} (${ret.code})`);
        }
        if (ret?.__typename !== "DataImportPipelineJobSubmitOutput") {
          throw new Error("Failed to submit data import pipeline job");
        }

        // history: record as running
        const historyItem: DataImportProjectPipelineJobHistoryItem = {
          dataImportProjectId: projectId,
          jobType: "DataImportPipelineJob",
          action,
          jobId: beJobIds[0] ?? bcrJobIds[0] ?? "",
          jobRunId: ret.jobRunId,
          executedAt: new Date().toISOString(),
          status: "running",
          parameters: {
            batchEvidencesJobIds: beJobIds,
            batchChangeRequestsJobIds: bcrJobIds,
            dryRun,
            dryRunOptions: options.dryRunOptions ?? null,
            autoApprove: options.autoApprove ?? null,
          },
          response: ret,
        };
        await historyService.saveHistory("DataImportPipelineJob", historyItem);

        // Open the log viewer immediately so the user can watch logs while we await the outcome.
        if (setLogDialogJobRun) {
          setLogDialogJobRun({ jobRunId: ret.jobRunId, jobName: "DataImportPipelineJob", dryRun });
        }

        // Detect real outcome from the pipeline job-log terminal marker.
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const outcome = await awaitRunOutcomeFromLogs(ret.jobRunId, {
          timeoutMs: 10 * 60 * 1000,
          signal: abortController.signal,
        });

        // Bail silently if the component unmounted (signal was aborted by the cleanup effect).
        if (abortController.signal.aborted) return;

        if (outcome === "succeeded") {
          setStatusMap(
            new Map([...beJobIds, ...bcrJobIds].map((id) => [id, (dryRun ? "dryok" : "success") as DipRunStatus])),
          );
          await historyService.updateHistoryItem("DataImportPipelineJob", ret.jobRunId, {
            ...historyItem,
            status: "completed",
          });
          if (!dryRun) {
            try {
              await onRefresh();
            } catch {
              // non-fatal
            }
          }
          const dryRunResult = toPipelineDryRunResultTarget({
            action,
            id: ret.jobRunId,
            status: "completed",
            batchEvidencesJobRuns: ret.batchEvidencesJobs,
            batchChangeRequestsJobRuns: ret.batchChangeRequestsJobs,
          });
          setAlert({
            type: "success",
            titleKey: dryRun ? "run.banner.dryRunComplete" : "run.banner.runComplete",
            jobRunId: ret.jobRunId,
            dryRunResult: dryRunResult ?? undefined,
          });
        } else if (outcome === "failed") {
          setStatusMap(new Map([...beJobIds, ...bcrJobIds].map((id) => [id, "failed" as DipRunStatus])));
          await historyService.updateHistoryItem("DataImportPipelineJob", ret.jobRunId, {
            ...historyItem,
            status: "failed",
          });
          if (!dryRun) {
            try {
              await onRefresh();
            } catch {
              // non-fatal
            }
          }
          setAlert({ type: "error", titleKey: "run.banner.runFailed", jobRunId: ret.jobRunId });
        } else {
          // timeout — leave history as "running"; show timeout banner
          setAlert({ type: "error", titleKey: "run.banner.runTimeout", jobRunId: ret.jobRunId });
        }
      } catch (_e) {
        setStatusMap(new Map([...beJobIds, ...bcrJobIds].map((id) => [id, "failed" as DipRunStatus])));
        setAlert({ type: "error", titleKey: "run.banner.runFailed" });
      } finally {
        setRunningAction(null);
      }
    },
    [historyService, onRefresh, projectId, runningAction, setLogDialogJobRun, submitMutation, tenantId],
  );

  return { running: runningAction !== null, runningAction, statusMap, alert, setAlert, run };
}
